import { ParentalConsentMapper } from "@/data/mapper/parental-consent-info-mapper";
import {
  AddAppAccountRequestPayload,
  TermsConsent,
} from "@/data/payload/app-account-setting/add-app-account-request-payload";
import { CheckUserIdAvailableRequestPayload } from "@/data/payload/app-account-setting/check-user-id-available-reqeuest-payload";
import { AppAccountSettingSource } from "@/data/source/app-account-setting-source";
import { AppAccountSettingRepository } from "@/domain/repository/app-account-setting-repository";
import { SATError, SATResult } from "@sadtx/sat-error";
import { safeMapToEntity } from "./safe-map-to-entity";

export class AppAccountSettingRepositoryImpl implements AppAccountSettingRepository {
  constructor(private readonly appAccountSettingSource: AppAccountSettingSource) {}

  async setAuthorizedToken(token: string): Promise<SATResult<void, SATError>> {
    const result = await this.appAccountSettingSource.setAuthorizedToken(token);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  async isExistAuthorizedToken(): Promise<boolean> {
    const result = await this.appAccountSettingSource.isExistAuthorizedToken();
    return result;
  }

  async removeAuthorizedToken(): Promise<void> {
    await this.appAccountSettingSource.removeAuthorizedToken();
  }

  async saveTermsConsent(termsConsent: TermsConsent[]): Promise<SATResult<void, SATError>> {
    const result = await this.appAccountSettingSource.saveTermsConsent(termsConsent);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  /**
   * SMS 토큰을 사용하여 로그인 계정의 사용자 이름을 검증합니다.
   * @param {CheckUserNameBySmsTokenRequestPayload} payload - 사용자 이름 및 SMS 토큰 정보
   * @returns {Promise<SATResult<void, SATError>>} 검증 결과
   */
  async checkUserNameBySmsTokenForLoginAccount(
    name: string
  ): Promise<SATResult<void, SATError>> {
    const result = await this.appAccountSettingSource.checkUserNameBySmsTokenForLoginAccount(name);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  /**
   * SMS를 통해 로그인 계정의 아이디와 비밀번호를 설정합니다.
   * @param {AddAppAccountRequestPayload} payload - 아이디 및 비밀번호 설정 정보
   * @returns {Promise<SATResult<void, SATError>>} 설정 결과
   */
  async addAppAccount(
    payload: AddAppAccountRequestPayload
  ): Promise<SATResult<void, SATError>> {
    const result = await this.appAccountSettingSource.addAppAccount(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  /**
   * 사용자 아이디의 사용 가능 여부를 확인합니다.
   * @param {CheckUserIdAvailableRequestPayload} payload - 확인할 아이디 정보
   * @returns {Promise<SATResult<void, SATError>>} 확인 결과
   */
  async checkUserIdAvailable(
    payload: CheckUserIdAvailableRequestPayload
  ): Promise<SATResult<void, SATError>> {
    const result = await this.appAccountSettingSource.checkUserIdAvailable(payload);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return SATResult.success(void 0);
  }

  /**
   * 개인정보 처리방침 파일(markdown)을 조회합니다.
   * @param {string} locale - 조회할 언어 로케일 코드
   * @returns {Promise<SATResult<ParentalConsentInfo, SATError>>} 개인정보 처리방침 파일 조회 결과
   */
  async fetchPrivayMdFile(locale: string): Promise<SATResult<ParentalConsentInfo, SATError>> {
    const result = await this.appAccountSettingSource.fetchPrivayMdFile(locale);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      ParentalConsentMapper.toInfo,
      "AppAccountSettingRepositoryImpl::fetchPrivayMdFile"
    );
  }

  /**
   * 서비스 이용약관 파일(markdown)을 조회합니다.
   * @param {string} locale - 조회할 언어 로케일 코드
   * @returns {Promise<SATResult<ParentalConsentInfo, SATError>>} 서비스 이용약관 파일 조회 결과
   */
  async fetchTermsOfServiceFile(
    locale: string
  ): Promise<SATResult<ParentalConsentInfo, SATError>> {
    const result = await this.appAccountSettingSource.fetchTermsOfServiceFile(locale);
    if (result.isFailure) {
      return SATResult.failure(result.error);
    }
    return safeMapToEntity(
      result.value,
      ParentalConsentMapper.toInfo,
      "AppAccountSettingRepositoryImpl::fetchTermsOfServiceFile"
    );
  }
}
